import React, { useEffect, useState } from "react";
import { applySeoOverride } from "../hooks/useSeo";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Briefcase, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { CITIES } from "../mock";

export default function JobsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cityParam = params.get("city") || "";
  const serviceParam = params.get("service") || "";
  const [jobs, setJobs] = useState(null);
  const [applied, setApplied] = useState(new Set());

  useEffect(() => {
    document.title = "Caregiver & Nanny Jobs Near You | SimplySitter";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.setAttribute("content", "Browse open nanny, babysitter, housekeeper and senior care jobs from families near you. Apply free on SimplySitter.");
    applySeoOverride();
    const qs = [];
    if (cityParam) qs.push(`city_slug=${cityParam}`);
    if (serviceParam) qs.push(`service=${serviceParam}`);
    const q = qs.length ? `?${qs.join("&")}` : "";
    api.get(`/jobs/public${q}`).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
    if (user?.role === "caregiver") {
      api.get("/applications/mine").then(({ data }) => setApplied(new Set(data.map((a) => a.job_id)))).catch(() => {});
    }
  }, [cityParam, serviceParam, user]);

  const setFilter = (key, value) => {
    const next = {};
    if (cityParam) next.city = cityParam;
    if (serviceParam) next.service = serviceParam;
    if (value) next[key] = value; else delete next[key];
    setParams(next);
  };

  const apply = async (job) => {
    if (!user) { navigate("/auth?role=caregiver&redirect=/jobs"); return; }
    if (user.role !== "caregiver") { toast.error("Only caregivers can apply to jobs."); return; }
    try {
      await api.post("/applications", { job_id: job.id, message: `Hi, I'd love to help with your ${job.service} role.` });
      toast.success("Application sent!");
      setApplied((s) => new Set([...s, job.id]));
    } catch (err) {
      const d = err?.response?.data?.detail || "Could not apply.";
      toast.error(d);
      if (d.includes("approved")) navigate("/caregiver");
    }
  };

  return (
    <div>
      <Header />
      <section className="hero-gradient">
        <div className="max-w-5xl mx-auto px-5 pt-14 pb-10">
          <h1 className="font-display text-4xl sm:text-5xl text-[#1f1a1b]">Open jobs{cityParam ? ` in ${CITIES.find((c) => c.slug === cityParam)?.name || ""}` : ""}</h1>
          <p className="text-[17px] text-[#5a5254] mt-3">Browse live jobs from families across the US. Apply once your caregiver profile is approved.</p>
          <div className="mt-5 flex flex-wrap gap-3 max-w-2xl">
            <select value={serviceParam} onChange={(e) => setFilter("service", e.target.value)}
              className="h-11 rounded-xl border border-black/10 px-3 text-[15px] bg-white outline-none focus:border-[#7c2233] min-w-[180px]">
              <option value="">All categories</option>
              <option value="nanny">Nanny</option>
              <option value="babysitter">Babysitter</option>
              <option value="housekeeper">Housekeeper</option>
              <option value="senior">Senior care</option>
            </select>
            <select value={cityParam} onChange={(e) => setFilter("city", e.target.value)}
              className="h-11 rounded-xl border border-black/10 px-3 text-[15px] bg-white outline-none focus:border-[#7c2233] min-w-[180px]">
              <option value="">All cities</option>
              {CITIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}, {c.state}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="bg-white min-h-[40vh]">
        <div className="max-w-5xl mx-auto px-5 py-12">
          {jobs === null ? (
            <div className="flex items-center gap-2 text-[#7c2233]"><Loader2 className="w-5 h-5 animate-spin" /> Loading jobs…</div>
          ) : jobs.length === 0 ? (
            <div className="rounded-2xl border border-black/10 p-10 text-center">
              <Briefcase className="w-8 h-8 text-[#8a8285] mx-auto" />
              <p className="text-[15px] text-[#5a5254] mt-3">No open jobs right now. Check back soon — new jobs are added daily.</p>
              <Link to="/cities" className="inline-flex mt-4 rounded-xl bg-[#7c2233] text-white px-5 py-3 font-medium">Browse cities</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((j) => (
                <div key={j.id} className="rounded-2xl border border-black/10 bg-white p-5 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="font-display text-lg text-[#1f1a1b] capitalize">{j.service} — {j.location}</h3>
                    <p className="text-[13px] text-[#8a8285] flex items-center gap-1.5 mt-1"><MapPin className="w-3.5 h-3.5" />{j.location}</p>
                    {j.details && <p className="text-[14px] text-[#5a5254] mt-2 max-w-xl">{j.details}</p>}
                  </div>
                  {applied.has(j.id) ? (
                    <span className="text-[13px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">Applied</span>
                  ) : (
                    <button onClick={() => apply(j)} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-white bg-[#7c2233] hover:bg-[#661b29] rounded-lg px-4 py-2 transition-colors"><Send className="w-4 h-4" /> Apply</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
